import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './queryKeys';
import type {
  CreateJobAlertRequest, Guid, InboxCountsDto, JobAlertDto, NotificationGroup, NotificationPageDto, NotificationSettingsDto,
} from './types';

export const inboxApi = {
  notifications: (unreadOnly: boolean, cursor?: string | null, limit = 30) =>
    api<NotificationPageDto>('/api/me/notifications', { query: { unreadOnly: unreadOnly || null, cursor: cursor ?? null, limit } }),
  counts: () => api<InboxCountsDto>('/api/me/inbox-counts'),
  markRead: (body: { ids?: Guid[]; all?: boolean }) => api<void>('/api/me/notifications/read', { method: 'POST', body }),
  settings: () => api<NotificationSettingsDto>('/api/me/notification-settings'),
  saveSettings: (body: { emailEnabled: boolean; mutedGroups: NotificationGroup[] }) =>
    api<NotificationSettingsDto>('/api/me/notification-settings', { method: 'PUT', body }),
  alerts: () => api<JobAlertDto[]>('/api/me/job-alerts'),
  createAlert: (body: CreateJobAlertRequest) => api<JobAlertDto>('/api/me/job-alerts', { method: 'POST', body }),
  deleteAlert: (id: Guid) => api<void>(`/api/me/job-alerts/${id}`, { method: 'DELETE' }),
};

/** Unread counts for the shell; kept fresh by the live connection (and a slow poll as a fallback). */
export function useInboxCounts(enabled = true) {
  return useQuery({ queryKey: qk.inbox.counts, queryFn: inboxApi.counts, enabled, refetchInterval: 120_000, staleTime: 30_000 });
}

export function useNotifications(unreadOnly: boolean, limit = 30) {
  return useInfiniteQuery({
    queryKey: [...qk.inbox.notifications(unreadOnly), limit],
    queryFn: ({ pageParam }) => inboxApi.notifications(unreadOnly, pageParam, limit),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inboxApi.markRead,
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.inbox.all }),
  });
}

export function useNotificationSettings() {
  return useQuery({ queryKey: qk.inbox.settings, queryFn: inboxApi.settings });
}

export function useSaveNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: inboxApi.saveSettings, onSuccess: (s) => qc.setQueryData(qk.inbox.settings, s) });
}

export function useJobAlerts(enabled = true) {
  return useQuery({ queryKey: qk.inbox.alerts, queryFn: inboxApi.alerts, enabled });
}

export function useCreateJobAlert() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: inboxApi.createAlert, onSuccess: () => void qc.invalidateQueries({ queryKey: qk.inbox.alerts }) });
}

export function useDeleteJobAlert() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: inboxApi.deleteAlert, onSuccess: () => void qc.invalidateQueries({ queryKey: qk.inbox.alerts }) });
}
